import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  ChevronDown,
  PenTool,
  Plus,
  FileText,
  MoreVertical,
  Eye,
  Send,
  RefreshCw,
  Ban,
  Trash2,
  X,
} from "lucide-react";
import { esignApi } from "../../services/api";

interface Signer {
  role: string;
  name: string;
  email: string;
  order: number;
  status: string;
  signedAt?: string;
}

interface EnvelopeData {
  _id: string;
  subject: string;
  message?: string;
  status: "draft" | "sent" | "viewed" | "signed" | "voided" | "expired";
  matterId?: { _id: string; title?: string } | string;
  signers: Signer[];
  documents: any[];
  createdAt: string;
  updatedAt?: string;
}

const statusConfig: Record<string, { label: string; bg: string; text: string }> = {
  draft: { label: "Draft", bg: "bg-slate-100", text: "text-slate-600" },
  sent: { label: "Sent", bg: "bg-blue-100", text: "text-blue-700" },
  viewed: { label: "Viewed", bg: "bg-amber-100", text: "text-amber-700" },
  signed: { label: "Signed", bg: "bg-emerald-100", text: "text-emerald-700" },
  voided: { label: "Voided", bg: "bg-red-100", text: "text-red-700" },
  expired: { label: "Expired", bg: "bg-slate-100", text: "text-slate-500" },
};

const statuses = ["draft", "sent", "viewed", "signed", "voided", "expired"];

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getMatterTitle(matterId: EnvelopeData["matterId"]): string {
  if (!matterId) return "-";
  if (typeof matterId === "string") return "-";
  return matterId.title || "-";
}

const PRIMARY = "var(--color-primary)";
const HOVER = "var(--color-primary-hover)";

interface EsignEnvelopesTabProps {
  refreshKey?: number;
}

const EsignEnvelopesTab: React.FC<EsignEnvelopesTabProps> = ({ refreshKey }) => {
  const navigate = useNavigate();

  // Data state
  const [envelopes, setEnvelopes] = useState<EnvelopeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  // Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // Kebab menu
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Void reason prompt
  const [voidTarget, setVoidTarget] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState("");

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  // Refs
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPagination((p) => ({ ...p, page: 1 }));
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchTerm]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(e.target as Node)
      ) {
        setShowStatusDropdown(false);
      }
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Fetch envelopes
  const fetchEnvelopes = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page: pagination.page,
        limit: 20,
      };
      if (statusFilter) params.status = statusFilter;
      if (debouncedSearch) params.search = debouncedSearch;

      const res = await esignApi.list(params);
      setEnvelopes(res.data?.data || []);
      setPagination((prev) => ({
        ...prev,
        pages: res.data?.pagination?.pages || 1,
        total: res.data?.pagination?.total || 0,
      }));
    } catch (err) {
      console.error("Error fetching envelopes:", err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedSearch, pagination.page]);

  useEffect(() => {
    fetchEnvelopes();
  }, [fetchEnvelopes, refreshKey]);

  // Clear selection on data change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [envelopes]);

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.size === envelopes.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(envelopes.map((e) => e._id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Bulk actions
  const handleBulkAction = async (action: string) => {
    if (selectedIds.size === 0) return;

    if (action === "void") {
      setVoidTarget("bulk");
      return;
    }

    if (action === "delete") {
      setDeleteTarget("bulk");
      return;
    }

    setBulkLoading(true);
    try {
      await esignApi.bulk(action, Array.from(selectedIds));
      setSelectedIds(new Set());
      fetchEnvelopes();
    } catch (err) {
      console.error(`Bulk ${action} failed:`, err);
    } finally {
      setBulkLoading(false);
    }
  };

  // Row actions
  const handleSend = async (id: string) => {
    setActionLoading(id);
    setOpenMenuId(null);
    try {
      await esignApi.send(id);
      fetchEnvelopes();
    } catch (err) {
      console.error("Failed to send envelope:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResend = async (id: string) => {
    setActionLoading(id);
    setOpenMenuId(null);
    try {
      await esignApi.resend(id);
      fetchEnvelopes();
    } catch (err) {
      console.error("Failed to resend envelope:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleVoidConfirm = async () => {
    if (!voidTarget) return;
    setActionLoading(voidTarget);
    try {
      if (voidTarget === "bulk") {
        setBulkLoading(true);
        await esignApi.bulk("void", Array.from(selectedIds), voidReason);
        setSelectedIds(new Set());
        setBulkLoading(false);
      } else {
        await esignApi.voidEnvelope(voidTarget, voidReason);
      }
      setVoidTarget(null);
      setVoidReason("");
      fetchEnvelopes();
    } catch (err) {
      console.error("Failed to void:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setActionLoading(deleteTarget);
    try {
      if (deleteTarget === "bulk") {
        setBulkLoading(true);
        await esignApi.bulk("delete", Array.from(selectedIds));
        setSelectedIds(new Set());
        setBulkLoading(false);
      } else {
        await esignApi.softDelete(deleteTarget);
      }
      setDeleteTarget(null);
      fetchEnvelopes();
    } catch (err) {
      console.error("Failed to delete:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const isAllSelected = envelopes.length > 0 && selectedIds.size === envelopes.length;

  return (
    <div className="space-y-4">
      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search signing requests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
          />
        </div>

        {/* Status filter */}
        <div className="relative" ref={statusDropdownRef}>
          <button
            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 hover:border-slate-300 transition-colors"
          >
            <Filter className="h-4 w-4" />
            {statusFilter
              ? statusConfig[statusFilter]?.label || statusFilter
              : "All Statuses"}
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          {showStatusDropdown && (
            <div className="absolute top-full mt-1 right-0 z-20 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[160px]">
              <button
                onClick={() => {
                  setStatusFilter("");
                  setShowStatusDropdown(false);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors"
              >
                All Statuses
              </button>
              {statuses.map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setStatusFilter(s);
                    setShowStatusDropdown(false);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors"
                >
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig[s]?.bg} ${statusConfig[s]?.text}`}
                  >
                    {statusConfig[s]?.label || s}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bulk Action Bar */}
      <AnimatePresence>
        {selectedIds.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-5 py-3"
          >
            <span className="text-sm font-medium text-slate-700">
              {selectedIds.size} selected
            </span>
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={() => handleBulkAction("void")}
                disabled={bulkLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                <Ban className="h-3.5 w-3.5" />
                Void
              </button>
              <button
                onClick={() => handleBulkAction("resend")}
                disabled={bulkLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Resend
              </button>
              <button
                onClick={() => handleBulkAction("delete")}
                disabled={bulkLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div
            className="animate-spin rounded-full h-8 w-8 border-b-2"
            style={{ borderColor: PRIMARY }}
          />
        </div>
      ) : envelopes.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-16 text-center"
        >
          <PenTool className="h-16 w-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No signing requests yet</h3>
          <p className="text-sm text-slate-500 mb-6">
            Create your first signing request to get started
          </p>
          <button
            onClick={() => navigate("/esign")}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-white font-medium rounded-xl transition-colors"
            style={{ backgroundColor: PRIMARY }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = HOVER;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = PRIMARY;
            }}
          >
            <Plus className="h-4 w-4" />
            New Request
          </button>
        </motion.div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Subject
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Matter
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Signers
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Documents
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Created
                </th>
                <th className="w-12 px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {envelopes.map((envelope, index) => {
                const config = statusConfig[envelope.status] || statusConfig.draft;
                const signedCount =
                  envelope.signers?.filter((s) => s.status === "signed").length || 0;
                const totalSigners = envelope.signers?.length || 0;
                const isSelected = selectedIds.has(envelope._id);
                const isMenuOpen = openMenuId === envelope._id;

                return (
                  <motion.tr
                    key={envelope._id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${
                      isSelected ? "bg-emerald-50/50" : ""
                    }`}
                  >
                    <td className="w-10 px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(envelope._id)}
                        className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                    </td>
                    <td
                      className="px-5 py-3"
                      onClick={() => navigate(`/esign/${envelope._id}`)}
                    >
                      <span className="text-sm font-medium text-slate-800 truncate block max-w-[220px]">
                        {envelope.subject}
                      </span>
                    </td>
                    <td
                      className="px-5 py-3"
                      onClick={() => navigate(`/esign/${envelope._id}`)}
                    >
                      <span className="text-sm text-slate-600">
                        {getMatterTitle(envelope.matterId)}
                      </span>
                    </td>
                    <td
                      className="px-5 py-3"
                      onClick={() => navigate(`/esign/${envelope._id}`)}
                    >
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
                      >
                        {config.label}
                      </span>
                    </td>
                    <td
                      className="px-5 py-3"
                      onClick={() => navigate(`/esign/${envelope._id}`)}
                    >
                      <span className="text-sm text-slate-600">
                        {signedCount}/{totalSigners} signed
                      </span>
                    </td>
                    <td
                      className="px-5 py-3"
                      onClick={() => navigate(`/esign/${envelope._id}`)}
                    >
                      <div className="flex items-center gap-1.5 text-sm text-slate-600">
                        <FileText className="h-3.5 w-3.5" />
                        {envelope.documents?.length || 0}
                      </div>
                    </td>
                    <td
                      className="px-5 py-3"
                      onClick={() => navigate(`/esign/${envelope._id}`)}
                    >
                      <span className="text-sm text-slate-500">
                        {formatDate(envelope.createdAt)}
                      </span>
                    </td>
                    <td className="w-12 px-3 py-3 relative" onClick={(e) => e.stopPropagation()}>
                      <div ref={isMenuOpen ? menuRef : null}>
                        <button
                          onClick={() => setOpenMenuId(isMenuOpen ? null : envelope._id)}
                          className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                          {actionLoading === envelope._id ? (
                            <div
                              className="animate-spin rounded-full h-4 w-4 border-b-2"
                              style={{ borderColor: PRIMARY }}
                            />
                          ) : (
                            <MoreVertical className="h-4 w-4 text-slate-400" />
                          )}
                        </button>

                        {isMenuOpen && (
                          <div className="absolute right-3 top-full mt-1 z-30 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[160px]">
                            <button
                              onClick={() => {
                                setOpenMenuId(null);
                                navigate(`/esign/${envelope._id}`);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors"
                            >
                              <Eye className="h-3.5 w-3.5" />
                              View
                            </button>

                            {envelope.status === "draft" && (
                              <button
                                onClick={() => handleSend(envelope._id)}
                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors"
                              >
                                <Send className="h-3.5 w-3.5" />
                                Send
                              </button>
                            )}

                            {(envelope.status === "sent" || envelope.status === "viewed") && (
                              <button
                                onClick={() => handleResend(envelope._id)}
                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors"
                              >
                                <RefreshCw className="h-3.5 w-3.5" />
                                Resend
                              </button>
                            )}

                            {(envelope.status === "draft" ||
                              envelope.status === "sent" ||
                              envelope.status === "viewed") && (
                              <button
                                onClick={() => {
                                  setOpenMenuId(null);
                                  setVoidTarget(envelope._id);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                              >
                                <Ban className="h-3.5 w-3.5" />
                                Void
                              </button>
                            )}

                            <button
                              onClick={() => {
                                setOpenMenuId(null);
                                setDeleteTarget(envelope._id);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            onClick={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
            disabled={pagination.page === 1}
            className="px-3 py-1.5 text-sm bg-white border border-slate-200 rounded-lg disabled:opacity-50 hover:bg-slate-50 transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-slate-600">
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            onClick={() =>
              setPagination((p) => ({ ...p, page: Math.min(p.pages, p.page + 1) }))
            }
            disabled={pagination.page === pagination.pages}
            className="px-3 py-1.5 text-sm bg-white border border-slate-200 rounded-lg disabled:opacity-50 hover:bg-slate-50 transition-colors"
          >
            Next
          </button>
        </div>
      )}

      {/* Void Reason Modal */}
      <AnimatePresence>
        {voidTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={() => {
              setVoidTarget(null);
              setVoidReason("");
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-xl border border-slate-200 p-6 w-full max-w-md mx-4"
            >
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Void Request</h3>
              <p className="text-sm text-slate-500 mb-4">
                {voidTarget === "bulk"
                  ? `This will void ${selectedIds.size} selected request${selectedIds.size !== 1 ? "s" : ""}. Please provide a reason.`
                  : "Please provide a reason for voiding this request."}
              </p>
              <textarea
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="Reason for voiding..."
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors resize-none"
              />
              <div className="flex items-center justify-end gap-3 mt-4">
                <button
                  onClick={() => {
                    setVoidTarget(null);
                    setVoidReason("");
                  }}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleVoidConfirm}
                  disabled={!voidReason.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  Void Request
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirm Modal */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={() => setDeleteTarget(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-xl border border-slate-200 p-6 w-full max-w-md mx-4"
            >
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Delete Request</h3>
              <p className="text-sm text-slate-500 mb-4">
                {deleteTarget === "bulk"
                  ? `Are you sure you want to delete ${selectedIds.size} selected request${selectedIds.size !== 1 ? "s" : ""}? This action cannot be undone.`
                  : "Are you sure you want to delete this signing request? This action cannot be undone."}
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EsignEnvelopesTab;
